import { Component } from '@theme/component';

const STORAGE_KEY = 'halue-compare-products';

/* ========================================================================
   Session Storage Utilities (exported for use by compare-button / compare-bar)
   ======================================================================== */

/**
 * @returns {string[]} Array of product handles
 */
export const getCompareHandles = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]') || [];
  } catch {
    return [];
  }
};

/**
 * @param {string} handle
 * @param {number} [max=3]
 * @returns {boolean} Whether the handle was added
 */
export const addCompareHandle = (handle, max = 4) => {
  const handles = getCompareHandles();
  if (handles.includes(handle)) return false;
  if (handles.length >= max) return false;

  handles.push(handle);
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  return true;
};

/**
 * @param {string} handle
 * @returns {boolean} Whether the handle was removed
 */
export const removeCompareHandle = (handle) => {
  const handles = getCompareHandles();
  const index = handles.indexOf(handle);
  if (index === -1) return false;

  handles.splice(index, 1);
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  return true;
};

/**
 * @param {string} handle
 * @returns {boolean}
 */
export const isInCompare = (handle) => {
  return getCompareHandles().includes(handle);
};

/* ========================================================================
   Component
   ======================================================================== */

/**
 * @typedef {Object} HalueProductCompareRefs
 * @property {HTMLElement} grid - Scroll container
 * @property {HTMLElement} gridInner - Grid element for cards
 * @property {HTMLElement} emptyState - Empty state container
 * @property {HTMLElement} loading - Loading spinner container
 * @property {HTMLButtonElement} resetBtn - Reset button
 */

/** @extends {Component<HalueProductCompareRefs>} */
class HalueProductCompare extends Component {
  /** @type {AbortController|null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.maxProducts = parseInt(this.dataset.maxProducts || '4') || 4;
    this.sectionName = this.dataset.sectionName;
    this.i18n = {
      yes: this.dataset.i18nYes || 'Yes',
      no: this.dataset.i18nNo || 'No',
    };
    this.blocksConfig = this.#parseBlocksConfig();
    this.#loadProducts();

    window.addEventListener('halue-compare:updated', this.#handleExternalUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    window.removeEventListener('halue-compare:updated', this.#handleExternalUpdate);
  }

  /** @param {Event} event */
  handleReset(event) {
    event.preventDefault();
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('halue-compare-products-meta');
    this.refs.gridInner.innerHTML = '';
    this.#showEmptyState();
    this.#updateResetVisibility(0);
    window.dispatchEvent(new CustomEvent('halue-compare:updated'));
  }

  #parseBlocksConfig() {
    const script = this.querySelector('.halue-compare-blocks-config');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch {
      return [];
    }
  }

  async #loadProducts() {
    const handles = getCompareHandles();

    if (!handles.length) {
      this.#showEmptyState();
      return;
    }

    this.#hideEmptyState();
    this.#showLoading();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const signal = /** @type {AbortController} */ (this.#abortController).signal;

    const cards = await Promise.all(
      handles.slice(0, this.maxProducts).map(handle =>
        this.#fetchCard(handle, signal)
      )
    );

    this.refs.gridInner.innerHTML = '';
    let loadedCount = 0;

    for (const cardHtml of cards) {
      if (!cardHtml) continue;

      if (this.blocksConfig.length > 0) {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = cardHtml;
        const card = wrapper.querySelector('.halue-product-compare__card');

        if (card) {
          const metaScript = card.querySelector('.halue-product-compare__metafields');
          let customMeta = {};
          if (metaScript) {
            try { customMeta = JSON.parse(metaScript.textContent || '{}'); } catch { /* empty */ }
            metaScript.remove();
          }
          const refsScript = card.querySelector('.halue-product-compare__resolved-refs');
          if (refsScript) {
            try { Object.assign(customMeta, JSON.parse(refsScript.textContent || '{}')); } catch { /* empty */ }
            refsScript.remove();
          }
          if (card.dataset.productPrice) {
            customMeta['__price'] = card.dataset.productPrice;
          }
          const cta = card.querySelector('.halue-product-compare__cta');
          const groupsHtml = this.#buildGroupsHtml(customMeta, loadedCount);
          if (cta) {
            cta.insertAdjacentHTML('beforebegin', groupsHtml);
          } else {
            card.insertAdjacentHTML('beforeend', groupsHtml);
          }
          this.refs.gridInner.appendChild(card);
        } else {
          this.refs.gridInner.insertAdjacentHTML('beforeend', cardHtml);
        }
      } else {
        this.refs.gridInner.insertAdjacentHTML('beforeend', cardHtml);
      }

      loadedCount++;
    }

    if (loadedCount === 0) {
      this.#showEmptyState();
      this.#hideLoading();
      return;
    }

    this.refs.gridInner.style.setProperty('--hpc-cols', String(loadedCount));
    this.#hideLoading();
    this.refs.grid.hidden = false;
    this.#updateResetVisibility(loadedCount);
    this.#initAccordionSync();
    this.#initRemoveButtons();
    requestAnimationFrame(() => this.#syncRowHeights());
  }

  /**
   * @param {string} handle
   * @param {AbortSignal} signal
   * @returns {Promise<string|null>}
   */
  async #fetchCard(handle, signal) {
    try {
      const response = await fetch(
        `/products/${encodeURIComponent(handle)}?sections=${this.sectionName}`,
        { signal }
      );

      if (!response.ok) return null;

      const data = await response.json();
      return data[/** @type {string} */ (this.sectionName)] || null;
    } catch (/** @type {any} */ error) {
      if (error.name === 'AbortError') return null;
      console.error('HalueProductCompare: fetch error', error);
      return null;
    }
  }

  /**
   * @param {Object} customMeta - product.metafields.custom as key/value pairs
   * @param {number} cardIndex
   * @returns {string}
   */
  #buildGroupsHtml(customMeta, cardIndex) {
    let html = '';
    let groupIndex = 0;
    let inGroup = false;

    for (const block of this.blocksConfig) {
      if (block.type === 'group') {
        const open = block.settings.open_by_default ? ' open' : '';
        html += `<details class="halue-product-compare__group" data-group-index="${groupIndex}"${open}>`;
        html += `<summary class="halue-product-compare__summary">`;
        html += `<span>${this.#escapeHtml(block.settings.title || '')}</span>`;
        html += `<svg class="halue-product-compare__chevron" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        html += `</summary><div class="halue-product-compare__fields">`;
        groupIndex++;
        inGroup = true;
      } else if (block.type === 'field' && block.settings.metafield_key) {
        const mk = block.settings.metafield_key;
        const value = customMeta[mk];
        const valueHtml = this.#renderFieldValue(value, block.settings);
        const tooltip = block.settings.tooltip;
        html += `<div class="halue-product-compare__field">`;
        html += `<span class="halue-product-compare__field-label">`;
        html += `<span>${this.#escapeHtml(block.settings.label || '')}</span>`;
        if (tooltip) {
          const safeTooltip = this.#escapeHtml(tooltip);
          html += `<button type="button" class="halue-product-compare__tooltip" aria-label="${safeTooltip}">`;
          html += `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.2"/><path d="M7 6.2V10" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><circle cx="7" cy="4.2" r="0.7" fill="currentColor"/></svg>`;
          html += `<span class="halue-product-compare__tooltip-text" role="tooltip">${safeTooltip}</span>`;
          html += `</button>`;
        }
        html += `</span>`;
        html += `<span class="halue-product-compare__field-value">${valueHtml}</span>`;
        html += `</div>`;
      } else if (block.type === 'group_end' && inGroup) {
        html += `</div></details>`;
        inGroup = false;
      }
    }

    return html;
  }

  /**
   * @param {*} value
   * @param {Object} settings
   * @returns {string}
   */
  #renderFieldValue(value, settings) {
    const type = settings.display_type;

    // Auto-detect boolean by value (native true/false or 'true'/'false' string) so it
    // works even when display_type is left as 'text' but the metafield itself is boolean.
    // Must run BEFORE the null/'' guard so native `false` is not treated as empty.
    const isBoolValue = value === true || value === false || value === 'true' || value === 'false';
    if (type === 'boolean' || isBoolValue) {
      if (value == null || value === '') {
        return `<span class="halue-comparison-field__empty" aria-label="N/A">—</span>`;
      }
      const isTrue = value === true || value === 'true';
      const mod = isTrue ? 'yes' : 'no';
      const label = isTrue ? this.i18n.yes : this.i18n.no;
      return `<span class="halue-comparison-field__bool halue-comparison-field__bool--${mod}">${this.#escapeHtml(label)}</span>`;
    }

    if (value == null || value === '') {
      return `<span class="halue-comparison-field__empty" aria-label="N/A">—</span>`;
    }

    if (type === 'rating') {
      const ratingVal = parseInt(value) || 0;
      const ratingMax = settings.rating_max || 5;
      let bars = '';
      for (let i = 1; i <= ratingMax; i++) {
        bars += `<span class="halue-comparison-field__bar${i <= ratingVal ? ' halue-comparison-field__bar--filled' : ''}"></span>`;
      }
      return `<div class="halue-comparison-field__rating" role="img" aria-label="${ratingVal} / ${ratingMax}">${bars}</div>`;
    }

    if (type === 'number_with_unit') {
      const unit = settings.unit ? `<span class="halue-comparison-field__unit">${this.#escapeHtml(settings.unit)}</span>` : '';
      return `<span class="halue-comparison-field__number">${this.#escapeHtml(String(value))}${unit}</span>`;
    }

    return `<span class="halue-comparison-field__text">${this.#escapeHtml(String(value))}</span>`;
  }

  /** @param {string} str */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  #initAccordionSync() {
    const container = this.refs.gridInner;
    if (!container) return;

    let syncing = false;

    container.addEventListener('toggle', (e) => {
      const trigger = /** @type {HTMLDetailsElement} */ (e.target);
      if (syncing || !trigger.matches?.('.halue-product-compare__group[data-group-index]')) return;

      const idx = trigger.getAttribute('data-group-index');
      const targets = container.querySelectorAll(
        `.halue-product-compare__group[data-group-index="${idx}"]`
      );

      syncing = true;
      for (const target of /** @type {NodeListOf<HTMLDetailsElement>} */ (targets)) {
        if (target !== trigger) target.open = trigger.open;
      }
      syncing = false;

      requestAnimationFrame(() => this.#syncRowHeights());
    }, true);
  }

  #syncRowHeights() {
    const container = this.refs.gridInner;
    if (!container) return;

    const cards = Array.from(container.querySelectorAll('.halue-product-compare__card'));
    if (cards.length < 2) return;

    const summaryCount = cards[0].querySelectorAll('.halue-product-compare__summary').length;
    for (let i = 0; i < summaryCount; i++) {
      const summaries = cards
        .map(c => c.querySelectorAll('.halue-product-compare__summary')[i])
        .filter(Boolean);
      summaries.forEach(el => { /** @type {HTMLElement} */ (el).style.minHeight = ''; });
      const maxH = Math.max(...summaries.map(el => /** @type {HTMLElement} */ (el).offsetHeight));
      if (maxH > 0) summaries.forEach(el => { /** @type {HTMLElement} */ (el).style.minHeight = `${maxH}px`; });
    }

    const openGroups = container.querySelectorAll('.halue-product-compare__group[open]');
    const groupIndices = new Set(
      Array.from(openGroups).map(g => /** @type {HTMLElement} */ (g).dataset.groupIndex)
    );

    for (const idx of groupIndices) {
      const allGroups = Array.from(
        container.querySelectorAll(`.halue-product-compare__group[data-group-index="${idx}"]`)
      );
      const fieldRows = allGroups.map(g =>
        Array.from(g.querySelectorAll('.halue-product-compare__field'))
      );
      if (!fieldRows[0]?.length) continue;

      const fieldCount = fieldRows[0].length;
      for (let f = 0; f < fieldCount; f++) {
        const fields = fieldRows.map(rows => /** @type {HTMLElement} */ (rows[f])).filter(Boolean);
        fields.forEach(el => { el.style.minHeight = ''; });
        const maxH = Math.max(...fields.map(el => el.offsetHeight));
        if (maxH > 0) fields.forEach(el => { el.style.minHeight = `${maxH}px`; });
      }
    }
  }

  #initRemoveButtons() {
    const container = this.refs.gridInner;
    if (!container) return;

    container.addEventListener('click', (e) => {
      const removeBtn = /** @type {HTMLElement} */ (e.target).closest('.halue-product-compare__remove');
      if (!removeBtn) return;

      e.preventDefault();
      const handle = removeBtn.dataset.handle;
      if (!handle) return;

      removeCompareHandle(handle);
      window.dispatchEvent(new CustomEvent('halue-compare:updated'));

      const card = removeBtn.closest('.halue-product-compare__card');
      if (card) card.remove();

      const remaining = container.querySelectorAll('.halue-product-compare__card').length;
      container.style.setProperty('--hpc-cols', String(remaining));
      this.#updateResetVisibility(remaining);
      requestAnimationFrame(() => this.#syncRowHeights());

      if (remaining === 0) {
        this.#showEmptyState();
        this.refs.grid.hidden = true;
      }
    });
  }

  #showEmptyState() {
    if (this.refs.emptyState) this.refs.emptyState.hidden = false;
    if (this.refs.grid) this.refs.grid.hidden = true;
  }

  #hideEmptyState() {
    if (this.refs.emptyState) this.refs.emptyState.hidden = true;
  }

  #showLoading() {
    if (this.refs.loading) this.refs.loading.hidden = false;
  }

  #hideLoading() {
    if (this.refs.loading) this.refs.loading.hidden = true;
  }

  /** @param {number} count */
  #updateResetVisibility(count) {
    if (this.refs.resetBtn) this.refs.resetBtn.hidden = count === 0;
  }

  #handleExternalUpdate = () => {
    this.#loadProducts();
  };
}

customElements.define('halue-product-compare', HalueProductCompare);
